import React, { useEffect, useState } from "react";
import { useParams, useHistory, Redirect } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Modal from "../../components/Modal";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import SETTINGS_RESOURCES, { findSettingsResource, RESOURCE_KEY_TO_MENU_URL } from "./settingsResources";
import "../Users/UsersPage.css";
import "./Settings.css";

const emptyForm = { name: "", serial: 0, status: 1, extraFieldValue: "" };

/**
 * One page renders all 13 "simple" settings resources (everything in
 * settingsResources.js except teacher-sections, which has its own page —
 * see TeacherSectionsPage) — the frontend mirror of the backend's generic
 * AbstractLookupController.
 */
export default function LookupSettingsPage({ resourceKey: fixedResourceKey }) {
  const { resourceKey: paramResourceKey } = useParams();
  const resourceKey = fixedResourceKey || paramResourceKey;
  const history = useHistory();
  const resource = findSettingsResource(resourceKey);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [modalMode, setModalMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const api = resource ? createLookupApi(resource.path) : null;

  const load = () => {
    if (!api) return;
    setLoading(true);
    api
      .list()
      .then(setItems)
      .catch(() => setError("Failed to load " + resource.label.toLowerCase() + "."))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [resourceKey]);

  if (!resource) {
    return <Redirect to="/dashboard" />;
  }

  const extraField = resource.extraField;

  const openCreate = () => {
    setForm(emptyForm);
    setModalMode("create");
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      serial: item.serial,
      status: item.status,
      extraFieldValue: extraField ? item[extraField.name] || "" : "",
    });
    setModalMode("edit");
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingId(null);
  };

  const buildPayload = () => {
    const payload = { name: form.name, serial: Number(form.serial) };
    if (modalMode === "edit") {
      payload.status = Number(form.status);
    }
    if (extraField) {
      payload[extraField.name] =
        extraField.type === "number" && form.extraFieldValue !== "" ? Number(form.extraFieldValue) : form.extraFieldValue || undefined;
    }
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      if (modalMode === "create") {
        await api.create(buildPayload());
        setSuccess(resource.label + " created.");
      } else {
        await api.update(editingId, buildPayload());
        setSuccess(resource.label + " updated.");
      }
      closeModal();
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) {
      return;
    }
    setError("");
    try {
      await api.remove(item.id);
      setSuccess(resource.label + " deleted.");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  const columns = [
    { key: "name", label: "Name" },
    ...(extraField ? [{ key: extraField.name, label: extraField.label }] : []),
    { key: "serial", label: "Serial" },
    { key: "status", label: "Status", render: (row) => (row.status === 1 ? "Active" : "Inactive") },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <div>
          <h1>{resource.label}</h1>
          <select
            className="settings-jump"
            value={resourceKey}
            onChange={(e) => {
              const nextKey = e.target.value;
              // Reached via the fixed /admin/settings/:resourceKey route (a
              // route param is present): keep jumping within that route.
              // Reached via the real per-institute menu url instead (this
              // component got resourceKey as a fixed prop, no route param):
              // jump to that resource's own real menu url so the same
              // permission-scoped route keeps handling it, rather than
              // bouncing a non-super-admin off the fixed super-admin route.
              const menuUrl = RESOURCE_KEY_TO_MENU_URL[nextKey];
              if (!paramResourceKey && menuUrl) {
                history.push(`/${menuUrl}`);
              } else {
                history.push(`/admin/settings/${nextKey}`);
              }
            }}
          >
            {SETTINGS_RESOURCES.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <button type="button" className="btn-primary" onClick={openCreate}>
          + Add {resource.label.replace(/s$/, "")}
        </button>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          actions={(item) => (
            <>
              <button type="button" className="btn-link" onClick={() => openEdit(item)}>
                Edit
              </button>
              <button type="button" className="btn-link danger" onClick={() => handleDelete(item)}>
                Delete
              </button>
            </>
          )}
        />
      )}

      {modalMode && (
        <Modal title={(modalMode === "create" ? "Add " : "Edit ") + resource.label.replace(/s$/, "")} onClose={closeModal}>
          <form onSubmit={handleSubmit} className="user-form">
            <label>Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />

            {extraField && extraField.editable && (
              <>
                <label>
                  {extraField.label}
                  {extraField.required ? " *" : ""}
                </label>
                <input
                  type={extraField.type === "number" ? "number" : "text"}
                  value={form.extraFieldValue}
                  onChange={(e) => setForm({ ...form, extraFieldValue: e.target.value })}
                  required={!!extraField.required}
                />
              </>
            )}

            {extraField && !extraField.editable && modalMode === "edit" && (
              <>
                <label>{extraField.label}</label>
                <input value={form.extraFieldValue === "" ? "(computed automatically)" : form.extraFieldValue} disabled />
              </>
            )}

            <label>Serial</label>
            <input
              type="number"
              value={form.serial}
              onChange={(e) => setForm({ ...form, serial: e.target.value })}
              required
            />

            {modalMode === "edit" && (
              <>
                <label>Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </>
            )}

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </MainLayout>
  );
}
